/**
 * WebCartPageViewModel - Web Platform Adapter
 * 
 * Web-specific implementation of CartPageViewModel for React web apps
 * Integrates with React Router and existing web services
 */

import { useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useCartStore, useRewardsStore, DeliveryService, PlacesService } from '@restaurant/core'
import type { CartState } from '@restaurant/core'
import { createCartPageViewModel, createWebPlatformServices, WebAddressService } from '@restaurant/viewmodels'
// CheckoutService/CartService are imported from the deep pages path: the barrel's
// canonical `CheckoutService` is the sheets (CheckoutViewModel) interface, whereas
// createCartPageViewModel consumes the pages CheckoutService (validateCartForCheckout /
// proceedToCheckout). Importing from the pages module gives the matching shapes.
import type {
  CartService,
  AddressService as CartAddressService,
} from '@restaurant/viewmodels/pages/CartPageViewModel'
import type { CheckoutService } from '@restaurant/viewmodels/pages/CartPageViewModel'
import { apiClient } from '@restaurant/api-client'

// Web-specific cart service implementation
const createWebCartService = (getCartStore: () => CartState): CartService => ({
  getCartItems: async () => {
    const store = getCartStore()
    return store.items || []
  },

  updateQuantity: async (itemId: string, quantity: number) => {
    const store = getCartStore()
    store.updateQuantity(itemId, quantity)
  },

  removeItem: async (itemId: string) => {
    const store = getCartStore()
    store.removeItem(itemId)
  },

  getSubtotal: async () => {
    const store = getCartStore()
    return store.subtotal || 0
  },

  clearCart: async () => {
    const store = getCartStore()
    store.clearCart()
  },

  getFreeItemIds: () => {
    return getCartStore().freeItemIds || []
  },

  getOfferDiscountValue: () => {
    const store = getCartStore()
    if (!store.appliedOfferId) return 100
    const offer = useRewardsStore.getState().getOfferById(store.appliedOfferId)
    return offer?.value ?? 100
  },
})

// Create a WebAddressService-backed AddressService that conforms to the cart page's
// AddressService interface ({ street, cityStateZip }). WebAddressService implements the
// platform-agnostic IAddressService, whose AddressSummary uses { addressLine1, addressLine2 };
// this adapter maps between the two so the cart page receives the shape it expects.
const createWebAddressServiceInstance = (): CartAddressService => {
  const deliveryService = new DeliveryService({
    apiClient,
  })

  const placesService = new PlacesService({
    apiClient,
  })

  const webAddressService = new WebAddressService(deliveryService, placesService)

  return {
    getDeliveryAddress: async () => {
      const address = await webAddressService.getDeliveryAddress()
      if (!address) return null
      return {
        street: address.addressLine1,
        cityStateZip: address.addressLine2,
      }
    },
    updateDeliveryAddress: async (address) => {
      // Map the cart page summary back to the IAddressService shape before delegating.
      await webAddressService.updateDeliveryAddress({
        addressLine1: address.street,
        addressLine2: address.cityStateZip,
      })
    },
  }
}

// Web-specific checkout service implementation
// Exported so that unit tests can instantiate it directly with a controlled store getter.
export const createWebCheckoutService = (getCartStore: () => CartState): CheckoutService => ({
  validateCartForCheckout: async () => {
    const store = getCartStore()
    const items = store.items || []

    if (items.length === 0) {
      return { isValid: false, errors: ['Your cart is empty'] }
    }

    // Block checkout when any item needs user review (e.g. missing required customizations
    // after cart restoration from a draft order).
    const hasItemsRequiringReview = items.some((item) => item.requiresReview === true)
    if (hasItemsRequiringReview) {
      return {
        isValid: false,
        errors: ['Some items require your review before checkout. Please update the highlighted items.'],
      }
    }

    const total = store.total || 0
    const minimumOrder = 10.00 // Example minimum order amount

    if (total < minimumOrder) {
      return {
        isValid: false,
        errors: [`Minimum order amount is $${minimumOrder.toFixed(2)}`]
      }
    }

    return { isValid: true, errors: [] }
  },

  proceedToCheckout: async () => {
    // In a real app, this might validate payment methods,
    // create an order draft, etc.
    return { success: true }
  }
})

/**
 * Web-specific CartPageViewModel hook
 * Integrates with existing web infrastructure and stores
 */
export const useCartPageViewModel = () => {
  const navigate = useNavigate()
  const location = useLocation()

  // Create web-specific platform services
  const platformServices = createWebPlatformServices(navigate, location)

  // Create service instances (memoized)
  const addressService = useMemo(() => createWebAddressServiceInstance(), [])

  // Create web-specific service dependencies
  // Use a getter function to avoid stale closures - always get current store state
  const dependencies = useMemo(() => ({
    cartService: createWebCartService(() => useCartStore.getState()),
    addressService: addressService,
    checkoutService: createWebCheckoutService(() => useCartStore.getState())
  }), [addressService])

  // Create and return the platform-agnostic CartPageViewModel
  const CartPageViewModelHook = createCartPageViewModel(platformServices, dependencies)
  return CartPageViewModelHook()
}

// Re-export useCheckoutViewModel so CartPage can import everything it needs from this module
export { useCheckoutViewModel } from '../sheets/WebCheckoutViewModel'